import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, HeartIcon, ListMusicIcon, PencilIcon, PlayIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import { usePlayer } from '../contexts/PlayerContext';
import { usePageMeta } from '../hooks/usePageMeta';
export function Library() {
  const {
    favorites,
    playlists,
    createPlaylist,
    renamePlaylist,
    deletePlaylist
  } = useLibrary();
  const {
    play
  } = usePlayer();
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  usePageMeta({
    title: 'Your Library',
    description: 'Your Noctuzan sets — playlists built from real iTunes previews, stored in this browser and playable end to end.',
    image: playlists[0]?.tracks[0]?.artwork,
    path: '/library'
  });
  return <div className="px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-308" data-mp="3gz3c2dsjgrv0">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-309" data-mp="3emiwtcf7gqlf">
        <div data-id="element-310" data-mp="39n6b4v02lpkf">
          <h1 className="font-display text-4xl text-cream" data-id="element-311" data-mp="39meq2e43na8e">Your Library</h1>
          <p className="mt-2 text-sm text-muted" data-id="element-312" data-mp="35lbbrw1p9m9a">
            {playlists.length} {playlists.length === 1 ? 'set' : 'sets'}, stored in this browser — they survive a
            refresh.
          </p>
        </div>
        <Link to="/favorites" className="flex w-fit items-center gap-2 rounded-full border border-ink-line px-4 py-2.5 text-sm text-cream transition-colors duration-150 ease-night hover:border-iris-soft/60 hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-313" data-mp="16v0pq6jubym">
          <HeartIcon className="h-4 w-4" aria-hidden="true" data-id="element-314" data-mp="3jk1afg463tzr" />
          Favourites
          <span className="tabular-nums text-muted" data-id="element-315" data-mp="kvlu7b9m3mcw">{favorites.length}</span>
        </Link>
      </div>

      <form onSubmit={e => {
      e.preventDefault();
      if (!name.trim()) return;
      createPlaylist(name);
      setName('');
    }} className="mt-8 flex max-w-md gap-2" data-id="element-316" data-mp="g43v6sr5fvuk">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Name a new set…" aria-label="New playlist name" className="min-w-0 flex-1 rounded-full border border-ink-line bg-ink-panel px-4 py-2.5 text-sm text-cream placeholder:text-muted focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-317" data-mp="13ye1g8vs85a7" />
        <button type="submit" className="flex items-center gap-1.5 rounded-full bg-gold px-4 py-2.5 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-318" data-mp="lasj4s3zhqu6">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-319" data-mp="25pcduzzxk80e" />
          Create
        </button>
      </form>

      {playlists.length === 0 ? <p className="mt-10 max-w-md text-sm leading-relaxed text-muted" data-id="element-320" data-mp="3q7d34tx9f6r4">
          No sets yet. Name one above, or use the <span className="text-cream" data-id="element-321" data-mp="3m3owcdggcf6x">＋</span> beside any track to build one
          as you listen.
        </p> : <ul className="mt-9 grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-322" data-mp="3v6gail0ppp29">
          {playlists.map((playlist, i) => {
        const covers = playlist.tracks.slice(0, 4);
        const renaming = editingId === playlist.id;
        return <motion.li key={playlist.id} layout initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} whileHover={{
          y: -6,
          rotate: i % 2 === 0 ? -0.8 : 0.8
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1],
          delay: Math.min(i, 8) * 0.03
        }} className="group relative flex gap-4 overflow-hidden rounded-lg border border-ink-line bg-ink-panel/70 p-4 transition-colors duration-200 ease-night hover:border-gold/40" data-id="element-323" data-mp="8q3rtfolk1qy" data-mpi={i}>
                <span aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-300 ease-night group-hover:opacity-100" style={{
            background: 'radial-gradient(circle, rgb(var(--accent) / 0.35) 0%, rgb(var(--accent) / 0) 70%)'
          }} data-id="element-324" data-mp="2pydo7g16tehk" />
                <div className="relative grid h-20 w-20 flex-none grid-cols-2 grid-rows-2 overflow-hidden rounded bg-ink-raised ring-1 ring-cream/5" data-id="element-325" data-mp="279753rwhekv8">
                  {covers.length === 0 && <span className="col-span-2 row-span-2 flex items-center justify-center" data-id="element-326" data-mp="2gz0dyl58olvs">
                      <ListMusicIcon className="h-6 w-6 text-muted" aria-hidden="true" data-id="element-327" data-mp="1ji1p9cqjjskx" />
                    </span>}
                  {covers.map(track => <img key={track.id} src={track.artworkSmall} alt="" loading="lazy" className={`h-full w-full object-cover transition-transform duration-300 ease-night group-hover:scale-105 ${covers.length === 1 ? 'col-span-2 row-span-2' : ''}`} data-id="element-328" data-mp="1gyok9jun9nby" />)}
                </div>
                <div className="relative min-w-0 flex-1" data-id="element-329" data-mp="t8u8z6pz46tt">
                  {renaming ? <form onSubmit={e => {
              e.preventDefault();
              renamePlaylist(playlist.id, draft);
              setEditingId(null);
            }} className="flex gap-2" data-id="element-330" data-mp="t6zk5hbkzukg">
                      <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} aria-label={`Rename ${playlist.name}`} className="min-w-0 flex-1 rounded border border-ink-line bg-ink-base px-2 py-1.5 text-sm text-cream focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-331" data-mp="a9yfjxlxvwh1" />
                      <button type="submit" aria-label="Save name" className="flex h-8 w-8 flex-none items-center justify-center rounded bg-gold text-ink-base focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-332" data-mp="3rsxaywlzkre2">
                        <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-333" data-mp="9l7qwr6suxle" />
                      </button>
                    </form> : <h2 className="truncate font-display text-xl text-cream" data-id="element-334" data-mp="12ay0qo1efdbp">
                      <Link to={`/playlist/${playlist.id}`} className="rounded transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-335" data-mp="172ob2u0c6134">
                        {playlist.name}
                      </Link>
                    </h2>}
                  <p className="mt-1 truncate text-xs text-muted" data-id="element-336" data-mp="1l7gdx6tnhulk">
                    {playlist.tracks.length} {playlist.tracks.length === 1 ? 'track' : 'tracks'}
                    {playlist.note ? ` · ${playlist.note}` : ''}
                  </p>
                  <div className="mt-3 flex items-center gap-1.5" data-id="element-337" data-mp="tivbd3773qo8">
                    <button type="button" onClick={() => play(playlist.tracks, 0)} disabled={playlist.tracks.length === 0} aria-label={`Play ${playlist.name}`} className="flex items-center gap-1.5 rounded-full border border-ink-line px-3 py-1.5 text-xs text-cream transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-338" data-mp="17hlb6qleg4ga">
                      <PlayIcon className="h-3 w-3" aria-hidden="true" data-id="element-339" data-mp="l8egqqa9fw69" />
                      Play
                    </button>
                    <button type="button" onClick={() => {
                setDraft(playlist.name);
                setEditingId(renaming ? null : playlist.id);
              }} aria-label={`Rename ${playlist.name}`} className="rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-340" data-mp="pzwgxc4glsq6">
                      <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-341" data-mp="35rk3qepre4v9" />
                    </button>
                    <button type="button" onClick={() => deletePlaylist(playlist.id)} aria-label={`Delete ${playlist.name}`} className="rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-342" data-mp="3apc662o159dp">
                      <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-343" data-mp="2oelm3l5chdp8" />
                    </button>
                  </div>
                </div>
              </motion.li>;
      })}
        </ul>}
    </div>;
}